// Render a date picker that only allows to chose a fixed week
// Source: https://github.com/veljkocukic/react-week-picker
import React, { useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';

import {
  WEEK_START_DAY_NUMBER,
  addDays,
  addMonths,
  daysLabels,
  getDaysInMonth,
  getEndOfWeek,
  getIsSameDay,
  getStartOfWeek,
  subDays,
  subMonths,
} from '@isd/lib/helpers/dates';

import './weekPickerStyle.css';

export type WeekObject = {
  firstDay: Date;
  lastDay: Date;
  selectedDay?: Date; // The day of the week that was clicked on
};

export default function WeekPicker({
  startDate,
  endDate,
  selectedDate,
  onChange,
}: {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
  onChange: (week: WeekObject) => void;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(startDate || new Date());
  const [week, setWeek] = useState({
    firstDay: startDate || getStartOfWeek(),
    lastDay: endDate || getEndOfWeek(),
  });

  const isLeapYear = () => {
    let leapYear = new Date(new Date().getFullYear(), 1, 29);
    return leapYear.getDate() == 29;
  };

  const convertDate = (date: Date) => {
    let dt = new Date(date);

    return `${dt.getDate()} ${months[dt.getMonth()]}`;
  };

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLDivElement;
    const clickedDateString = target.getAttribute('data-date');
    if (!clickedDateString) {
      // This case should ideally not be reached if data-date is always set on rendered day elements.
      console.error('Clicked element is missing data-date attribute.');
      return;
    }
    const clickedDate = new Date(clickedDateString);

    // Update the internal 'date' state to reflect the month of the clicked day.
    // This ensures the calendar view updates to the correct month if a day from
    // a different month was clicked.
    setDate(clickedDate);

    const firstDay = getStartOfWeek(clickedDate);
    const lastDay = getEndOfWeek(clickedDate);
    // The clickedDate is already the selectedDay
    const selectedWeek = { firstDay, lastDay, selectedDay: clickedDate };
    setWeek(selectedWeek);

    if (onChange) {
      onChange(selectedWeek);
    }
  };

  const months = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun',
    'July',
    'Aug.',
    'Sep.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];

  const days = {
    1: 31,
    2: isLeapYear() ? 29 : 28,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };

  type MonthNumber = keyof typeof days;

  const renderDays = () => {
    let month = (date.getMonth() + 1) as MonthNumber;
    let daysInCurrentMonth = [];
    for (let i = 1; i <= days[month]; i++) {
      let currentDate = new Date(date).setDate(i);
      let cName = 'single-number ';
      if (
        new Date(week.firstDay).getTime() <= new Date(currentDate).getTime() &&
        new Date(currentDate).getTime() <= new Date(week.lastDay).getTime()
      ) {
        cName = cName + 'selected-week';
        if (
          !!selectedDate &&
          getIsSameDay(new Date(currentDate), selectedDate)
        ) {
          cName = cName + ' selected-date';
        }
      }

      daysInCurrentMonth.push(
        <div
          key={currentDate}
          id={`${i}`}
          className={cName}
          onClick={handleClick}
          data-date={new Date(currentDate).toISOString()}
        >
          {i}
        </div>
      );
    }

    const displayDate = new Date(date).setDate(1);
    let dayInTheWeek = new Date(displayDate).getDay();
    if (dayInTheWeek < WEEK_START_DAY_NUMBER) {
      dayInTheWeek = WEEK_START_DAY_NUMBER + 6;
    }
    let prevMonth = [];
    let prevMonthNumber = new Date(date).getMonth() as MonthNumber | 0;
    if (prevMonthNumber === 0) {
      prevMonthNumber = 12;
    }
    let previousMonthFirstDay = new Date(
      `${date.getUTCFullYear()}-${prevMonthNumber}-01 12:00`
    );
    for (let i = dayInTheWeek; i > WEEK_START_DAY_NUMBER; i--) {
      const startOfWeekPreviousMonth =
        days[prevMonthNumber] - i + (WEEK_START_DAY_NUMBER + 1);
      let currentDate = new Date(previousMonthFirstDay).setDate(
        startOfWeekPreviousMonth
      );
      let cName = 'single-number other-month';
      let currentTime = new Date(currentDate).getTime();
      let firstTime = new Date(week.firstDay).getTime();
      let endTime = new Date(week.lastDay).getTime();
      if (currentTime >= firstTime && currentTime <= endTime) {
        cName = 'single-number selected-week';
        if (
          !!selectedDate &&
          getIsSameDay(new Date(currentDate), selectedDate)
        ) {
          cName = cName + ' selected-date';
        }
      }

      prevMonth.push(
        <div
          onClick={handleClick}
          key={'prev-' + i}
          id={'prev-' + i}
          className={cName}
          data-date={subDays(displayDate, i).toISOString()}
        >
          {startOfWeekPreviousMonth}
        </div>
      );
    }

    let nextMonth = [];
    // The calendar displays a total of 35 days (5 weeks) by default.
    // If the current month and the previous month together have more than 35 days,
    // then display 42 days (6 weeks) to accommodate all days.
    let fullDays = 35;
    if ([...prevMonth, ...daysInCurrentMonth].length > 35) {
      fullDays = 42;
    }

    for (
      let i = 1;
      i <= fullDays - [...prevMonth, ...daysInCurrentMonth].length;
      i++
    ) {
      const lastDayOfMonth = new Date(
        new Date(date).setDate(getDaysInMonth(date))
      );
      const currentDate = addDays(lastDayOfMonth, i);
      const currentTimestamp = currentDate.getTime();
      const firstTime = new Date(week.firstDay).getTime();
      const endTime = new Date(week.lastDay).getTime();
      let cName = 'single-number other-month';

      if (currentTimestamp >= firstTime && currentTimestamp <= endTime) {
        cName = 'single-number selected-week';
        if (!!selectedDate && getIsSameDay(currentDate, selectedDate)) {
          cName = cName + ' selected-date';
        }
      }

      nextMonth.push(
        <div
          onClick={handleClick}
          key={'next-' + i}
          id={'next-' + i}
          className={cName}
          data-date={currentDate.toISOString()}
        >
          {i}
        </div>
      );
    }
    return [...prevMonth, ...daysInCurrentMonth, ...nextMonth];
  };

  const onChangeMonth = (goForward = false) => {
    let localDate = new Date(date);
    if (goForward) {
      localDate = addMonths(localDate, 1);
    } else {
      localDate = subMonths(localDate, 1);
    }
    setDate(new Date(localDate));
  };

  return (
    <div
      className="week-picker-display"
      onBlur={() => setOpen(false)}
      onClick={() => setOpen(true)}
      tabIndex={0}
    >
      <p>
        {week.firstDay.getDate()} - {convertDate(week.lastDay)}
      </p>
      <CalendarIcon className="ml-4 size-6" />
      {open && (
        <div className="week-picker-options">
          <div className="title-week">
            <div onClick={() => onChangeMonth()} className="arrow-container">
              <ChevronLeftIcon className="size-6" />
            </div>
            {`${months[date.getMonth()]} ${date.getFullYear()}.`}
            <div
              onClick={() => onChangeMonth(true)}
              className="arrow-container"
            >
              <ChevronRightIcon className="size-6" />
            </div>
          </div>
          <div className="numbers-container border-t-base-150 border-t">
            {daysLabels.map((dayLabel) => (
              <div key={dayLabel} className="single-number day">
                {dayLabel}
              </div>
            ))}
          </div>
          <div className="numbers-container border-t-base-150 border-t">
            {renderDays()}
          </div>
        </div>
      )}
    </div>
  );
}
